// Simple "select a record to delete" dropdown for the record-based sections
// (risk location / coverage / interested party / equipment / tool floater /
// fine art) when the board's "Delete" action is chosen. A single dropdown,
// no checkboxes.

import React from "react";
import { Grid } from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../../../components/formsComponents";
import { IBusinessPolicyChangeStepsProps } from "../../PolicyChange.types";
import { useBusinessPolicyRiskLocation } from "../../../hooks/useBusinessPolicyRiskLocation";
import { useBusinessPolicyCoverage } from "../../../hooks/useBusinessPolicyCoverage";
import { useBusinessIntrestedParty } from "../../../hooks/useBusinessPolicyInterestedParty";
import { useBusinessPolicyEquipment } from "../../../hooks/useBusinessPolicyEquiment";
import { useBusinessPolicyToolFloater } from "../../../hooks/useBusinessPolicyToolFloater";
import { useBusinessPolicyFineArt } from "../../../hooks/useBusinessPolicyFineArt";
import { RecordSectionKey } from "./RecordPanelHost";

interface IDeletePickerV2Props extends IBusinessPolicyChangeStepsProps {
  section: RecordSectionKey;
}

const CONFIG: Record<
  IDeletePickerV2Props["section"],
  { label: string; field: string }
> = {
  riskLocationChange: {
    label: "Select Risk Location to Delete",
    field: "risk_location_change.delete_risk_location",
  },
  coverageChange: {
    label: "Select Coverage to Delete",
    field: "coverage_change.delete_coverage",
  },
  interestedPartyChange: {
    label: "Select Interested Party to Delete",
    field: "interested_party_change.delete_interested_party",
  },
  equipmentChange: {
    label: "Select Equipment to Delete",
    field: "equipment_change.delete_equipment",
  },
  toolFloaterChange: {
    label: "Select Tool Floater to Delete",
    field: "tool_floater_change.delete_tool_floater",
  },
  fineArtChange: {
    label: "Select Fine Art to Delete",
    field: "fine_art_change.delete_fine_art",
  },
};

export const DeletePickerV2: React.FC<IDeletePickerV2Props> = ({
  section,
  values,
  setFieldValue,
  customerPolicyId,
}) => {
  const { riskLocationsDropDown } =
    useBusinessPolicyRiskLocation(customerPolicyId);
  const { coverageDropdown } = useBusinessPolicyCoverage(customerPolicyId);
  const { interestedPartyDropDown } =
    useBusinessIntrestedParty(customerPolicyId);
  const { equipmentDropdown } = useBusinessPolicyEquipment(customerPolicyId);
  const { toolFloaterDropdown } = useBusinessPolicyToolFloater(customerPolicyId);
  const { fineArtDropdown } = useBusinessPolicyFineArt(customerPolicyId);

  const options =
    section === "riskLocationChange"
      ? riskLocationsDropDown
      : section === "coverageChange"
      ? coverageDropdown
      : section === "interestedPartyChange"
      ? interestedPartyDropDown
      : section === "equipmentChange"
      ? equipmentDropdown
      : section === "toolFloaterChange"
      ? toolFloaterDropdown
      : fineArtDropdown;

  const { label, field } = CONFIG[section];

  const currentValue =
    section === "riskLocationChange"
      ? values.risk_location_change.delete_risk_location
      : section === "coverageChange"
      ? values.coverage_change.delete_coverage
      : section === "interestedPartyChange"
      ? values.interested_party_change.delete_interested_party
      : section === "equipmentChange"
      ? values.equipment_change.delete_equipment
      : section === "toolFloaterChange"
      ? values.tool_floater_change.delete_tool_floater
      : values.fine_art_change.delete_fine_art;

  return (
    <Grid container>
      <Grid item xs={12} md={6}>
        <CustomFormLabel>{label}</CustomFormLabel>
        <ControlledCustomSelect
          variant="outlined"
          size="small"
          type="string"
          name={field}
          fullWidth
          displayEmpty
          value={currentValue ?? ""}
          // Write the field explicitly rather than relying on the event's
          // target.name propagating through the drawer's generic handler —
          // this guarantees the picked value lands on the right delete_*
          // field so the DELETE entry surfaces in "Changes Requested".
          onChange={(e: any) => setFieldValue(field, e.target.value)}
          placeholder="Select One"
          options={options as any}
          // Rendered inside the V2 right-side drawer (zIndex 1400); the menu
          // portals to the body at the default modal zIndex (1300) and would
          // open behind the drawer. Keep it above the drawer.
          MenuProps={{ sx: { zIndex: 1600 } }}
        />
      </Grid>
    </Grid>
  );
};
